/**
 * Holds definitions for our base objects for content
 *
 */

import { InvisualFilter } from '../domain/content/entities/valueobjects/InvisualFilter';
import { DataSetField, DataSetFields, Field, FieldDataTypes, Fields, FieldTypes, FormatTypes } from '../domain/content/entities/valueobjects/Fields';
import { cloneObject } from '../utilities';

export const getValueSelector = (fieldName, appearance): InvisualFilter => {
  return {
    type: 'valueSelector',
    fieldName: fieldName,
    options: {
      appearance: appearance,
      sort: 'asc',
      saveState: 'last',
      title: '',
      titleEnabled: false,
      showCharacteristic: {
        enabled: true,
        prefix: ' (',
        suffix: ')',
      },
      playButton: {
        enabled: true,
        position: 'left',
        loop: false,
        autoStart: false,
        interval: 2000,
      },
      buttonsStyle: {},
    },
  };
};

export const getMetrics = () => {
  return {
    impressions: 0,
  };
};

export const getDefaultDataSetField = (userLocale): DataSetField => {
  //console.log("userlocale",userLocale," user pref: ",user.userPreferences.lang);
  return {
    dataType: 'string',
    fieldName: 'default',
    fieldType: 'miscField',
    isoLabelField: null,
    isoIsoField: null,
    isoMappingId: null,
    title: 'default',
    format: {
      formatType: 'string',
      locale: userLocale,
      number: {
        numberFormat: '.0f',
        decimalPlaces: 0,
        digitGrouping: true,
        shorten: false,
        showSign: false,
        showZero: true,
      },
      date: {
        inputFormat: 'YYYY',
        dateFormat: 'YYYY',
      },
      prefix: '',
      suffix: '',
    },
  };
};

/**
 * object for the root folder
 * @param userId
 * @returns
 */
export const getRootObj = (userId) => {
  return {
    _id: 'root',
    slug: 'root',
    type: 'collection',
    userId: userId,
    shares: [],
    shareCoding: [],
  };
};

/**
 * used in wizard and create dataset to check data for
 * types
 * @returns
 */
export const getFieldMeta = () => {
  return {
    type: 'mixed',
    recommendation: 'mixed',
    recommendationCertainty: 0,
    count: 0, // all rows
    errors: 0, //alle die nicht mit type übereinstimmen und probleme machen z.b Date -> passt net aufs format, oder value -> a
    warnings: 0, //alle die nicht typ haben aber kein problem (empty string bei string, null bei numbers, label das nicht zugeordnet werden kann bei iso)
    distinct: 0, //alle unique von valid
    meta: {
      geo: {}, // iso parts
      date: {}, // input formatting, min,max, intervall?
      number: {}, // min max zeros median, aveage, percentiles, ...
      string: {}, // min max length, average length,... special chars spaces,..
    },
  };
};

export const getQuillFromField = (field: Field) => {  
  const quillLine = [
    {
      insert: {
        inlineValue: {
          fieldName: field.fieldName,
          fieldType: field.fieldType,
          title: field.title,
          format: cloneObject(field.format),
        },
      },
    },
    {
      insert: '\n',
    },
  ];

  const quillValue = {
    ops: [...quillLine],
  };

  return JSON.stringify(quillValue);
};

export const getQuillFromFields = (fields: Field[]) => {
  const quillLines = fields.flatMap((field) => [
    {
      insert: {
        inlineValue: {
          fieldName: field.fieldName,
          title: field.title,
          format: cloneObject(field.format),
        },
      },
    },
    {
      insert: '\n',
    },
  ]);

  const quillValue = {
    ops: [...quillLines],
  };

  return JSON.stringify(quillValue);
};

export const getQuillFromText = (text: string): string => {
  const quillLines = [
    { insert: text },
    { insert: '\n' },
  ];

  const quillValue = { ops: [...quillLines]};

  return JSON.stringify(quillValue);
};

// Allow quill to be used with custom variables in the form {{variable}}
export const getQuillFromVariables = (text: string): string => {

  const quillLines = getVariablesFromText(text)
  
  quillLines.push({ insert: '\n' })  
  const quillValue = { ops: [...quillLines]};

  return JSON.stringify(quillValue);
};

export const getVariablesFromText = (text: string) => {
  const regex = /{{\s*([\w.]+)\s*}}/g;
  const result:{ value: string; type: string }[]  = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    // Text before the match
    if (match.index > lastIndex) {
      result.push({ type: 'text', value: text.slice(lastIndex, match.index)});
    }
    // The variable match
    result.push({ type: 'variable', value: match[1]});
    // Update lastIndex
    lastIndex = regex.lastIndex;
  }

  // Any remaining text after the last match
  if (lastIndex < text.length) {
    result.push({ type: 'text', value: text.slice(lastIndex)});
  }
  
  return result.map((expr) => {
    if(expr.type === 'text') return {insert: expr.value}
    if(expr.type === 'variable') return { insert: { inlineVariable: { variableName: expr.value }}}
  })
}

/**
 * get qull value for donut
 */

export const getDonutQuillValue = (labelField: Field, valueField: Field) => {
  return JSON.stringify({
    ops: [
      {
        insert: {
          inlineValue: {
            fieldName: labelField.fieldName,
            title: labelField.title,
            format: cloneObject(labelField.format),
          },
        },
      },
      {
        attributes: {
          align: 'center',
        },
        insert: '\n',
      },
      {
        insert: {
          inlineValue: {
            fieldName: valueField.fieldName,
            title: valueField.title,
            format: cloneObject(valueField.format),
          },
        },
      },
      {
        attributes: {
          align: 'center',
        },
        insert: '\n',
      },
    ],
  });
};

export const getOldTooltips = (fields: Fields, labelFieldName, valueFieldName) => {
  //console.log('get tooltips', fields, visualOptions);
  const toolTips: any = {};
  toolTips.event = 'click';
  toolTips.type = 'old';
  toolTips.name = 'data:' + labelFieldName;
  toolTips.value = {
    label: '',
    field: 'data:' + valueFieldName,
    format: cloneObject(fields[valueFieldName].format),
  };
  toolTips.fields = [];
  return toolTips;
};

/**
 * input a DataSet just returns the fieldObject with default
 * uniqueness and aggregation parts
 */
export const getVisFields = (dataSetFields: DataSetFields): Fields => {
  const fields: Fields = cloneObject<any>(dataSetFields);
  for (const fieldName of Object.keys(fields)) {
    const currentField: any = fields[fieldName];
    currentField.isNeededForUniqueness = false;
    currentField.selectedValues = [];
  }
  return fields;
};

export const getMultiValueSelector = (fieldName, title) => {
  return {
    type: 'multiValueSelector',
    fieldName: fieldName,
    selectedValues: [],
    options: {
      appearance: 'dropdown',
      sort: 'asc',
      saveState: 'allSelected',
      title: title,
    },
  };
};
